import React, { useState, useEffect } from 'react';
import api from '../services/api'; 
import {
  Container, Paper, Typography, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, 
  Button, TextField, Box, Pagination, Dialog, DialogActions, DialogContent, DialogContentText, 
  DialogTitle, FormControl, InputLabel, Select, MenuItem, CircularProgress, Alert,
  Tabs, Tab
} from '@mui/material';
import { Edit, Check, Close, LockOpen, Search, Key, Refresh } from '@mui/icons-material';

const AdminDashboard = () => {
  // Tabs state
  const [tabValue, setTabValue] = useState(0);

  // Users tab state
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedUser, setSelectedUser] = useState(null);
  const [openFreeAccessDialog, setOpenFreeAccessDialog] = useState(false);
  const [freeAccessDuration, setFreeAccessDuration] = useState(30);
  const [subscriptionFilter, setSubscriptionFilter] = useState('all');
  const [processing, setProcessing] = useState(false);

  // Migration button state
  const [showMigrationConfirm, setShowMigrationConfirm] = useState(false);
  const [migrationStatus, setMigrationStatus] = useState(null);
  const [migrationResult, setMigrationResult] = useState(null);

  // Migration tab state
  const [migrationUsers, setMigrationUsers] = useState([]);
  const [migrationLoading, setMigrationLoading] = useState(true);
  const [migrationError, setMigrationError] = useState('');
  const [migrationFilterTerm, setMigrationFilterTerm] = useState('');
  const [openTempPasswordDialog, setOpenTempPasswordDialog] = useState(false);
  const [selectedMigrationUser, setSelectedMigrationUser] = useState(null);
  const [tempPassword, setTempPassword] = useState('');

  // Tab handling
  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
    if (newValue === 1 && migrationUsers.length === 0) {
      fetchMigrationStatus();
    }
  };

  // Ensure auth token is available for all requests
  const getAuthConfig = () => {
    const token = localStorage.getItem('token');
    return {
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    };
  };

  // Fetch users with explicit pagination and subscription filtering
  const fetchUsers = async (pageToFetch = page, subscriptionFilterValue = subscriptionFilter) => {
    setLoading(true);
    setError('');
    try {
      // Use the getAuthConfig to ensure token is always included
      const config = getAuthConfig();
      config.params = {
        page: pageToFetch,
        per_page: 10,
        search: searchTerm
      };
      
      // Add subscription filter if selected
      if (subscriptionFilterValue && subscriptionFilterValue !== 'all') {
        config.params.subscription = subscriptionFilterValue;
      }
      
      console.log(`Fetching users for page ${pageToFetch} with config:`, config);
      
      const response = await api.get('/admin/users', config);
      console.log('Users API response:', response.data);
      
      setUsers(response.data.users);
      setTotalPages(response.data.pages);
    } catch (err) {
      console.error('Error fetching users:', err);
      setError('Failed to load users. Please check your connection and try again.');
    } finally {
      setLoading(false);
    }
  };

  // Execute legacy payment migration
  const executeMigration = async () => {
    setMigrationStatus('running');
    try {
      const config = getAuthConfig();
      const response = await api.post('/admin/migrate-legacy-payments', {}, config);
      
      console.log('Migration response:', response.data);
      setMigrationResult(response.data);
      setMigrationStatus('success');
      setSuccess(`Migration completed successfully. Total users with lifetime access: ${response.data.total_lifetime_users || 0}`);
      
      // Refresh user list
      setTimeout(() => {
        fetchUsers();
      }, 1000);
      
    } catch (err) {
      console.error('Migration error:', err);
      setMigrationStatus('error');
      setError('Failed to migrate legacy users. Please try again or contact support.');
    } finally {
      setShowMigrationConfirm(false);
    }
  };

  // Format subscription information for display
  const formatSubscriptionInfo = (user) => {
    if (user.subscription_plan === 'Lifetime') {
      return 'Lifetime Access';
    } else if (user.subscription_expiry) {
      const expiryDate = new Date(user.subscription_expiry);
      const now = new Date();
      
      if (expiryDate > now) {
        const daysRemaining = Math.ceil((expiryDate - now) / (1000 * 60 * 60 * 24));
        return `${user.subscription_plan || 'Free'} (${daysRemaining} days left)`;
      } else {
        return 'Expired';
      }
    }
    
    return 'No active subscription';
  };

  // Helper function to determine badge color based on subscription status
  const getSubscriptionStatusBadgeColor = (user) => {
    if (user.subscription_plan === 'Lifetime') {
      return 'primary.main';
    } else if (user.payment_status === 'Active' && user.subscription_expiry) {
      const expiryDate = new Date(user.subscription_expiry);
      const now = new Date();
      
      if (expiryDate > now) {
        const daysRemaining = Math.ceil((expiryDate - now) / (1000 * 60 * 60 * 24));
        return daysRemaining <= 7 ? 'warning.light' : 'success.light';
      }
    }
    
    return user.payment_status === 'Active' ? 'success.light' : 
           user.payment_status === 'Pending' ? 'warning.light' : 'error.light';
  };

  // Fetch migration status
  const fetchMigrationStatus = async () => {
    setMigrationLoading(true);
    setMigrationError('');
    try {
      const config = getAuthConfig();
      const response = await api.get('/auth/admin-user-migration-status', config);
      setMigrationUsers(response.data.users);
    } catch (err) {
      console.error('Error fetching migration status:', err);
      setMigrationError('Failed to load migration status. Please try again.');
    } finally {
      setMigrationLoading(false);
    }
  };

  // Load users on initial render
  useEffect(() => {
    console.log('Initial load - fetching users for page 1');
    fetchUsers(1);
  }, []);

  // Handle page change - explicitly fetch the new page
  const handlePageChange = (event, newPage) => {
    console.log(`Page changed to ${newPage}`);
    setPage(newPage);
    fetchUsers(newPage);
  };

  // Handle search
  const handleSearch = (e) => {
    e.preventDefault();
    console.log(`Searching for "${searchTerm}"`);
    setPage(1); // Reset to first page when searching
    fetchUsers(1);
  };

  // Toggle user verification status
  const toggleVerification = async (userId, currentStatus) => {
    try {
      const endpoint = currentStatus 
        ? `/admin/users/${userId}/unverify` 
        : `/admin/users/${userId}/verify`;
      
      await api.post(endpoint, {}, getAuthConfig());
      
      // Update local state
      setUsers(users.map(user => 
        user.id === userId 
          ? { ...user, verified: !currentStatus } 
          : user
      ));
      
      setSuccess(`User ${currentStatus ? 'unverified' : 'verified'} successfully`);
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      console.error('Error toggling verification:', err);
      setError('Failed to update user verification status. Please try again.');
      setTimeout(() => setError(''), 3000);
    }
  };

  // Toggle user approval status (keep this for backwards compatibility)
  const toggleApproval = async (userId, currentStatus) => {
    try {
      const endpoint = currentStatus 
        ? `/admin/users/${userId}/disapprove` 
        : `/admin/users/${userId}/approve`;
      
      await api.post(endpoint, {}, getAuthConfig());
      
      // Update local state
      setUsers(users.map(user => 
        user.id === userId 
          ? { ...user, is_approved: !currentStatus } 
          : user
      ));
      
      setSuccess(`User ${currentStatus ? 'disapproved' : 'approved'} successfully`);
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      console.error('Error toggling approval:', err);
      setError('Failed to update user status. Please try again.');
      setTimeout(() => setError(''), 3000);
    }
  };

  // Open free access dialog
  const openFreeAccess = (user) => {
    setSelectedUser(user);
    setOpenFreeAccessDialog(true);
  };

  // Grant free access
  const grantFreeAccess = async () => {
    setProcessing(true);
    
    try {
      const config = getAuthConfig();
      const accessDuration = freeAccessDuration === 'lifetime' 
        ? 'lifetime'
        : parseInt(freeAccessDuration);
        
      await api.post(`/admin/users/${selectedUser.id}/free-access`, { 
        duration_days: accessDuration,
        plan: freeAccessDuration === 'lifetime' ? 'Lifetime' : 'Free'
      }, config);
      
      // Update local state
      setUsers(users.map(user => 
        user.id === selectedUser.id 
          ? { 
              ...user, 
              payment_status: 'Active',
              subscription_plan: freeAccessDuration === 'lifetime' ? 'Lifetime' : 'Free',
              subscription_expiry: freeAccessDuration === 'lifetime' ? '2099-12-31' : null,
              // Also update verified status for lifetime users
              verified: freeAccessDuration === 'lifetime' ? 1 : user.verified
            } 
          : user
      ));
      
      setSuccess(`${freeAccessDuration === 'lifetime' ? 'Lifetime' : 'Free'} access granted successfully`);
      setOpenFreeAccessDialog(false);
      fetchUsers(); // Refresh user list to get updated data
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      console.error('Error granting access:', err);
      setError('Failed to grant access. Please try again.');
      setTimeout(() => setError(''), 3000);
    } finally {
      setProcessing(false);
    }
  };

  // Generate random password
  const generateRandomPassword = () => {
    const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
    let result = '';
    for (let i = 0; i < 10; i++) {
      result += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    return result;
  };

  // Open temp password dialog
  const openTempPassword = (user) => {
    setSelectedMigrationUser(user);
    setTempPassword(generateRandomPassword());
    setOpenTempPasswordDialog(true);
  };

  // Set temporary password
  const setUserTempPassword = async () => {
    try {
      const config = getAuthConfig();
      await api.post('/auth/admin-set-temp-password', { 
        email: selectedMigrationUser.email,
        temp_password: tempPassword
      }, config);
      
      setSuccess(`Temporary password set for ${selectedMigrationUser.email}`);
      setOpenTempPasswordDialog(false);
      
      // Refresh migration status
      fetchMigrationStatus();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      console.error('Error setting temp password:', err);
      setMigrationError('Failed to set temporary password. Please try again.');
      setTimeout(() => setMigrationError(''), 3000);
    }
  };

  // Copy to clipboard
  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    setSuccess('Copied to clipboard');
    setTimeout(() => setSuccess(''), 3000);
  };

  // Filter migration users
  const filteredMigrationUsers = migrationUsers.filter(user => 
    user.email.toLowerCase().includes(migrationFilterTerm.toLowerCase())
  );

  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
      <Typography variant="h4" gutterBottom>
        Admin Dashboard
      </Typography>
      
      <Tabs value={tabValue} onChange={handleTabChange} sx={{ mb: 3 }}>
        <Tab label="User Management" />
        <Tab label="Password Migration" />
      </Tabs>
      
      {tabValue === 0 ? (
        // USER MANAGEMENT TAB
        <>
          {/* Alerts */}
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}
          
          {success && (
            <Alert severity="success" sx={{ mb: 2 }}>
              {success}
            </Alert>
          )}

          {/* Search and Filter Bar */}
          <Paper sx={{ p: 2, mb: 4 }}>
            <Box component="form" onSubmit={handleSearch}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <TextField
                  fullWidth
                  label="Search users by email or name"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  sx={{ mr: 2 }}
                />
                <Button 
                  type="submit" 
                  variant="contained" 
                  startIcon={<Search />}
                >
                  Search
                </Button>
              </Box>
              
              {/* Subscription filter */}
              <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
                <FormControl sx={{ minWidth: 200 }}>
                  <InputLabel>Subscription Type</InputLabel>
                  <Select
                    size="small"
                    value={subscriptionFilter || 'all'}
                    label="Subscription Type"
                    onChange={(e) => {
                      setSubscriptionFilter(e.target.value);
                      setPage(1);
                      fetchUsers(1, e.target.value);
                    }}
                  >
                    <MenuItem value="all">All</MenuItem>
                    <MenuItem value="lifetime">Lifetime</MenuItem>
                    <MenuItem value="active">Active (Non-lifetime)</MenuItem>
                    <MenuItem value="expired">Expired</MenuItem>
                    <MenuItem value="none">No Subscription</MenuItem>
                  </Select>
                </FormControl>
                
                {subscriptionFilter && subscriptionFilter !== 'all' && (
                  <Button 
                    variant="outlined" 
                    size="small" 
                    onClick={() => {
                      setSubscriptionFilter('all');
                      setPage(1);
                      fetchUsers(1, 'all');
                    }}
                  >
                    Clear Filter
                  </Button>
                )}
              </Box>
            </Box>
          </Paper>
          
          {/* Migration Status Button - CORRECTED */}
          <Paper sx={{ p: 3, mb: 3 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Box>
                <Typography variant="h6" gutterBottom>
                  Legacy User Migration
                </Typography>
                <Typography variant="body2">
                  Migrate users with verified=1 (paid users) and admin users to have lifetime subscription access.
                  <strong> This will also correct any previous migration that incorrectly used is_approved=1.</strong>
                </Typography>
                <Alert severity="warning" sx={{ mt: 2 }}>
                  Note: Only 98 users have verified=1 (paid users), while all 223 users have is_approved=1.
                  This migration will ensure only the correct users receive lifetime access.
                </Alert>
              </Box>
              <Button 
                variant="contained" 
                color="primary"
                onClick={() => setShowMigrationConfirm(true)}
                disabled={migrationStatus === 'running'}
                startIcon={migrationStatus === 'running' ? <CircularProgress size={20} /> : null}
              >
                {migrationStatus === 'running' ? 'Migrating...' : 'Migrate Legacy Users'}
              </Button>
            </Box>
            
            {migrationStatus === 'success' && migrationResult?.corrected && (
              <Alert severity="success" sx={{ mt: 2 }}>
                Migration completed successfully. Corrected previous migration and
                properly set lifetime access for verified users and admins.
                Total users with lifetime access: {migrationResult?.total_lifetime_users || 0}
              </Alert>
            )}
            
            {migrationStatus === 'success' && !migrationResult?.corrected && (
              <Alert severity="success" sx={{ mt: 2 }}>
                Migration completed successfully. 
                Total users with lifetime access: {migrationResult?.total_lifetime_users || 0}
              </Alert>
            )}
            
            {migrationStatus === 'error' && (
              <Alert severity="error" sx={{ mt: 2 }}>
                Error occurred during migration. Please try again.
              </Alert>
            )}
          </Paper>
          
          {/* Users Table */}
          <Paper>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Sr.No</TableCell>
                    <TableCell>First Name</TableCell>
                    <TableCell>Last Name</TableCell>
                    <TableCell>Email</TableCell>
                    <TableCell>Date</TableCell>
                    <TableCell>Verified</TableCell>
                    <TableCell>Approved</TableCell>
                    <TableCell>Payment Status</TableCell>
                    <TableCell>Plan</TableCell>
                    <TableCell>Expiry</TableCell>
                    <TableCell>Action</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {loading ? (
                    <TableRow>
                      <TableCell colSpan={11} align="center">
                        <CircularProgress />
                      </TableCell>
                    </TableRow>
                  ) : users.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={11} align="center">
                        No users found
                      </TableCell>
                    </TableRow>
                  ) : (
                    users.map((user, index) => (
                      <TableRow key={user.id}>
                        <TableCell>{(page - 1) * 10 + index + 1}</TableCell>
                        <TableCell>{user.first_name}</TableCell>
                        <TableCell>{user.last_name}</TableCell>
                        <TableCell>{user.email}</TableCell>
                        <TableCell>{user.date}</TableCell>
                        <TableCell>
                          {user.verified ? (
                            <Check color="success" />
                          ) : (
                            <Close color="error" />
                          )}
                        </TableCell>
                        <TableCell>
                          {user.is_approved ? (
                            <Check color="success" />
                          ) : (
                            <Close color="error" />
                          )}
                        </TableCell>
                        <TableCell>
                          <Box sx={{ 
                            bgcolor: user.payment_status === 'Active' ? 'success.light' : 
                                    user.payment_status === 'Pending' ? 'warning.light' : 'error.light',
                            px: 1,
                            py: 0.5,
                            borderRadius: 1,
                            display: 'inline-block'
                          }}>
                            {user.payment_status || 'None'}
                          </Box>
                        </TableCell>
                        <TableCell>
                          {user.subscription_plan === 'Lifetime' ? (
                            <Box sx={{ 
                              bgcolor: 'primary.main',
                              color: 'white',
                              px: 1,
                              py: 0.5,
                              borderRadius: 1,
                              display: 'inline-block'
                            }}>
                              Lifetime
                            </Box>
                          ) : (
                            user.subscription_plan || 'None'
                          )}
                        </TableCell>
                        <TableCell>
                          {user.subscription_plan === 'Lifetime' ? (
                            <Typography variant="body2" fontStyle="italic">
                              Never expires
                            </Typography>
                          ) : (
                            user.subscription_expiry ? (
                              <Box sx={{ 
                                display: 'flex', 
                                alignItems: 'center',
                                color: new Date(user.subscription_expiry) < new Date() ? 'error.main' : 'inherit'
                              }}>
                                {user.subscription_expiry}
                                {new Date(user.subscription_expiry) < new Date() && (
                                  <Typography variant="body2" color="error" sx={{ ml: 1 }}>
                                    (Expired)
                                  </Typography>
                                )}
                              </Box>
                            ) : 'N/A'
                          )}
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                            <Box sx={{ display: 'flex', gap: 1 }}>
                              <Button
                                variant="contained"
                                color={user.verified ? "error" : "success"}
                                size="small"
                                onClick={() => toggleVerification(user.id, user.verified)}
                              >
                                {user.verified ? "Unverify" : "Verify"}
                              </Button>
                              
                              <Button
                                variant="contained"
                                color="primary"
                                size="small"
                                startIcon={<LockOpen />}
                                onClick={() => openFreeAccess(user)}
                              >
                                Grant Access
                              </Button>
                            </Box>
                            
                            <Button
                              variant="outlined"
                              color={user.is_approved ? "error" : "success"}
                              size="small"
                              onClick={() => toggleApproval(user.id, user.is_approved)}
                            >
                              {user.is_approved ? "Disapprove" : "Approve"}
                            </Button>
                          </Box>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
            
            {/* Pagination with explicit manual handling */}
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
              <Pagination 
                count={totalPages} 
                page={page} 
                onChange={handlePageChange} 
                color="primary" 
              />
            </Box>
          </Paper>
          
          {/* Free Access Dialog - Updated with Lifetime option */}
          <Dialog open={openFreeAccessDialog} onClose={() => setOpenFreeAccessDialog(false)}>
            <DialogTitle>Grant Access</DialogTitle>
            <DialogContent>
              <DialogContentText>
                Provide access to {selectedUser?.first_name} {selectedUser?.last_name}.
              </DialogContentText>
              <FormControl fullWidth sx={{ mt: 2 }}>
                <InputLabel>Duration</InputLabel>
                <Select
                  value={freeAccessDuration}
                  label="Duration"
                  onChange={(e) => setFreeAccessDuration(e.target.value)}
                >
                  <MenuItem value={7}>7 days</MenuItem>
                  <MenuItem value={30}>30 days</MenuItem>
                  <MenuItem value={90}>90 days</MenuItem>
                  <MenuItem value={180}>180 days</MenuItem>
                  <MenuItem value={365}>1 year</MenuItem>
                  <MenuItem value="lifetime">Lifetime</MenuItem>
                </Select>
              </FormControl>
              {freeAccessDuration === 'lifetime' && (
                <Alert severity="info" sx={{ mt: 2 }}>
                  Lifetime access will never expire and is typically granted to paid users.
                  This will also mark the user as verified (verified=1).
                </Alert>
              )}
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setOpenFreeAccessDialog(false)}>Cancel</Button>
              <Button onClick={grantFreeAccess} variant="contained" color="primary">
                Grant Access
              </Button>
            </DialogActions>
          </Dialog>

          {/* Migration Confirmation Dialog - CORRECTED */}
          <Dialog open={showMigrationConfirm} onClose={() => setShowMigrationConfirm(false)}>
            <DialogTitle>Confirm Legacy User Migration</DialogTitle>
            <DialogContent>
              <DialogContentText>
                This will correctly apply lifetime subscription access to:
                <ul>
                  <li>Users with verified=1 (98 users who have paid)</li>
                  <li>Admin users</li>
                </ul>
                
                <strong>Important:</strong> This will also correct any previous migration that incorrectly 
                gave lifetime access to all users with is_approved=1 (all 223 users).
                
                This operation cannot be undone. Are you sure you want to proceed?
              </DialogContentText>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setShowMigrationConfirm(false)}>Cancel</Button>
              <Button 
                onClick={executeMigration} 
                variant="contained" 
                color="primary"
              >
                Confirm Migration
              </Button>
            </DialogActions>
          </Dialog>
        </>
      ) : (
        // PASSWORD MIGRATION TAB - no changes to this section
        <>
          <Typography variant="h5" gutterBottom>
            Password Migration Management
          </Typography>
          
          <Box sx={{ mb: 3 }}>
            <Alert severity="info">
              This tool helps manage users who are migrating from the CodeIgniter system to the new React system. 
              Users with old password formats will be prompted to reset their password on first login.
            </Alert>
          </Box>
          
          {/* Migration summary */}
          <Paper sx={{ p: 3, mb: 3 }}>
            <Typography variant="h6" gutterBottom>
              Migration Status
            </Typography>
            <Box display="flex" alignItems="center" justifyContent="space-between">
              <Typography>
                <strong>{migrationUsers.length}</strong> users still need to update their passwords
              </Typography>
              <Button 
                variant="outlined" 
                startIcon={<Refresh />}
                onClick={fetchMigrationStatus}
              >
                Refresh
              </Button>
            </Box>
          </Paper>
          
          {/* Migration filter */}
          <Paper sx={{ p: 2, mb: 4 }}>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <TextField
                fullWidth
                label="Filter users by email"
                value={migrationFilterTerm}
                onChange={(e) => setMigrationFilterTerm(e.target.value)}
                sx={{ mr: 2 }}
              />
            </Box>
          </Paper>
          
          {/* Migration alerts */}
          {migrationError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {migrationError}
            </Alert>
          )}
          
          {success && (
            <Alert severity="success" sx={{ mb: 2 }}>
              {success}
            </Alert>
          )}
          
          {/* Migration Users Table */}
          <Paper>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>ID</TableCell>
                    <TableCell>Email</TableCell>
                    <TableCell>Username</TableCell>
                    <TableCell>Password Format</TableCell>
                    <TableCell>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {migrationLoading ? (
                    <TableRow>
                      <TableCell colSpan={5} align="center">
                        <CircularProgress />
                      </TableCell>
                    </TableRow>
                  ) : filteredMigrationUsers.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={5} align="center">
                        No users found
                      </TableCell>
                    </TableRow>
                  ) : (
                    filteredMigrationUsers.map((user) => (
                      <TableRow key={user.id}>
                        <TableCell>{user.id}</TableCell>
                        <TableCell>{user.email}</TableCell>
                        <TableCell>{user.username}</TableCell>
                        <TableCell>{user.password_format}</TableCell>
                        <TableCell>
                          <Button
                            variant="contained"
                            color="primary"
                            size="small"
                            startIcon={<Key />}
                            onClick={() => openTempPassword(user)}
                          >
                            Set Temp Password
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
          
          {/* Temp Password Dialog */}
          <Dialog open={openTempPasswordDialog} onClose={() => setOpenTempPasswordDialog(false)}>
            <DialogTitle>Set Temporary Password</DialogTitle>
            <DialogContent>
              <DialogContentText>
                Setting temporary password for: <strong>{selectedMigrationUser?.email}</strong>
              </DialogContentText>
              <TextField
                fullWidth
                label="Temporary Password"
                value={tempPassword}
                onChange={(e) => setTempPassword(e.target.value)}
                margin="normal"
                InputProps={{
                  endAdornment: (
                    <Button 
                      variant="text" 
                      onClick={() => copyToClipboard(tempPassword)}
                      size="small"
                    >
                      Copy
                    </Button>
                  )
                }}
              />
              <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                The user will be required to change this password on their next login.
              </Typography>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setOpenTempPasswordDialog(false)}>Cancel</Button>
              <Button 
                onClick={setUserTempPassword} 
                variant="contained" 
                color="primary"
                disabled={tempPassword.length < 8}
              >
                Set Password
              </Button>
            </DialogActions>
          </Dialog>
        </>
      )}
    </Container>
  );
};

export default AdminDashboard;